/**
 * Subcontracting Event System
 * Handles event emissions for cross-module integration
 */

export interface SubcontractingEvent {
  type: 'subcon.dispatched' | 'subcon.received' | 'subcon.closed';
  timestamp: string;
  subContractId: string;
  subContractNumber: string;
  vendorId: string;
  vendorName: string;
  data: Record<string, any>;
}

export interface SubcontractDispatchedEvent extends SubcontractingEvent {
  type: 'subcon.dispatched';
  data: {
    jobCardIds: string[];
    operationIds: string[];
    workOrderIds: string[];
    workstationIds: string[];
    materialItems: Array<{
      itemCode: string;
      quantity: number;
      uom: string;
    }>;
    expectedReturnDate: string;
  };
}

export interface SubcontractReceivedEvent extends SubcontractingEvent {
  type: 'subcon.received';
  data: {
    jobCardIds: string[];
    operationIds: string[];
    receivedQuantities: Array<{
      jobCardId: string;
      qtyGood: number;
      qtyReject: number;
    }>;
    completedItems: Array<{
      itemCode: string;
      quantity: number;
      uom: string;
    }>;
  };
}

export interface SubcontractClosedEvent extends SubcontractingEvent {
  type: 'subcon.closed';
  data: {
    jobCardIds: string[];
    operationIds: string[];
    totalServiceCost: number;
    additionalCosts: number;
    transportCost: number;
    variance: number;
  };
}

/**
 * Event listeners registry
 */
type EventListener<T extends SubcontractingEvent> = (event: T) => Promise<void> | void;

class SubcontractingEventEmitter {
  private listeners: Map<string, EventListener<any>[]> = new Map();

  /**
   * Register an event listener
   */
  on<T extends SubcontractingEvent>(eventType: T['type'], listener: EventListener<T>) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)!.push(listener);
  }

  /**
   * Remove an event listener
   */
  off<T extends SubcontractingEvent>(eventType: T['type'], listener: EventListener<T>) {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      const index = eventListeners.indexOf(listener);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event to all registered listeners
   */
  async emit<T extends SubcontractingEvent>(event: T) {
    const eventListeners = this.listeners.get(event.type);
    if (eventListeners && eventListeners.length > 0) {
      // Execute all listeners concurrently
      const promises = eventListeners.map(listener => {
        try {
          return Promise.resolve(listener(event));
        } catch (error) {
          console.error(`Error in event listener for ${event.type}:`, error);
          return Promise.resolve();
        }
      });
      
      await Promise.allSettled(promises);
    }
  }

  /**
   * Get all registered listeners for debugging
   */
  getListeners() {
    return Array.from(this.listeners.entries()).map(([type, listeners]) => ({
      type,
      count: listeners.length
    }));
  }
}

// Global event emitter instance
export const subcontractingEvents = new SubcontractingEventEmitter();

/**
 * Helper function to create event objects
 */
export const createSubcontractingEvent = <T extends SubcontractingEvent>(
  type: T['type'],
  subContractId: string,
  subContractNumber: string,
  vendorId: string,
  vendorName: string,
  data: T['data']
): T => {
  return {
    type,
    timestamp: new Date().toISOString(),
    subContractId,
    subContractNumber,
    vendorId,
    vendorName,
    data
  } as T;
};
